import { Badge, Group, Modal, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { formatDate, formatMoney, formatRateBp } from '../../lib/format';

export interface PricePoint {
  id: string;
  sku: string | null;
  description: string | null;
  packQty: number;
  packUnit: string;
  priceMinor: number;
  perUnitMinor: number;
  currency: string;
  effectiveAt: string;
  supersededAt: string | null;
  changeBp: number | null;
  changeUnknownBecause: string | null;
}

export interface ItemPriceHistory {
  inventoryItemId: string;
  itemName: string | null;
  entries: PricePoint[];
  currentPerUnitMinor: number | null;
  currentCurrency: string | null;
  firstQuotedAt: string;
  changeSinceFirstBp: number | null;
}

export const priceHistoryPath = (supplierId: string, inventoryItemId?: string) =>
  `/admin/v1/suppliers/suppliers/${supplierId}/price-history${
    inventoryItemId ? `?inventoryItemId=${inventoryItemId}` : ''
  }`;

export function usePriceHistory(supplierId: string, inventoryItemId?: string, enabled = true) {
  return useQuery({
    queryKey: listKey(priceHistoryPath(supplierId), { inventoryItemId }),
    queryFn: () => rawApi<ItemPriceHistory[]>('GET', priceHistoryPath(supplierId, inventoryItemId)),
    enabled: enabled && Boolean(supplierId),
  });
}

/** Up is bad news here — this is what the bar pays. Red for a rise, green for a cut. */
function ChangeBadge({ point }: { point: PricePoint }) {
  if (point.changeBp === null) {
    return (
      <Text size="xs" c="dimmed">
        {point.changeUnknownBecause ? 'Different money' : 'First price on file'}
      </Text>
    );
  }
  if (point.changeBp === 0) {
    return (
      <Text size="xs" c="dimmed">
        No change
      </Text>
    );
  }
  return (
    <Badge size="sm" variant="light" color={point.changeBp > 0 ? 'red' : 'green'}>
      {point.changeBp > 0 ? '+' : ''}
      {formatRateBp(point.changeBp)}
    </Badge>
  );
}

/**
 * What they were charging in March.
 *
 * Every price is compared PER COUNTED UNIT, which is the only comparison that survives a change of
 * pack: a case of twelve replaced by a case of six at a lower printed figure is a rise, and reading
 * the two list prices side by side would say the opposite. The server does that arithmetic, so this
 * screen and the vendor comparison cannot disagree about what a bottle costs.
 */
export function PriceHistoryModal({
  supplierId,
  supplierName,
  inventoryItemId,
  itemName,
  onClose,
}: {
  supplierId: string;
  supplierName: string;
  /** Set when opened from one row of their price list; unset shows everything they sell. */
  inventoryItemId?: string;
  itemName?: string;
  onClose: () => void;
}) {
  const history = usePriceHistory(supplierId, inventoryItemId);
  const items = history.data ?? [];

  return (
    <Modal
      opened
      onClose={onClose}
      size="lg"
      title={
        itemName ? `What ${supplierName} has charged for ${itemName}` : `What ${supplierName} has charged`
      }
    >
      {history.isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : history.isError ? (
        <EmptyNote>
          Their prices could not be loaded. Nothing has been lost — try again in a moment.
        </EmptyNote>
      ) : items.length === 0 ? (
        <EmptyNote>No prices have ever been recorded for them, so there is no history to read yet.</EmptyNote>
      ) : (
        <Stack gap="lg">
          {items.map((item) => (
            <div key={item.inventoryItemId}>
              <Group justify="space-between" align="flex-end" mb={4}>
                <div>
                  <Text fw={600}>{item.itemName ?? 'Something no longer on file'}</Text>
                  <Text size="xs" c="dimmed">
                    {item.entries.length} {item.entries.length === 1 ? 'price' : 'prices'} on file since{' '}
                    {formatDate(item.firstQuotedAt)}
                  </Text>
                </div>
                {/* The headline: what they have done to this price over the whole time on file.
                    Absent across a change of currency, because there is no honest figure. */}
                {item.changeSinceFirstBp !== null && item.changeSinceFirstBp !== 0 && (
                  <Badge variant="light" color={item.changeSinceFirstBp > 0 ? 'red' : 'green'}>
                    {item.changeSinceFirstBp > 0 ? 'Up ' : 'Down '}
                    {formatRateBp(Math.abs(item.changeSinceFirstBp))} since {formatDate(item.firstQuotedAt)}
                  </Badge>
                )}
              </Group>

              <Table.ScrollContainer minWidth={480}>
                <Table verticalSpacing="xs" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Priced from</Table.Th>
                      <Table.Th ta="right">A {item.entries[0]?.packUnit ?? 'unit'}</Table.Th>
                      <Table.Th ta="right">Change</Table.Th>
                      <Table.Th>How they sold it</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {item.entries.map((point) => (
                      <Table.Tr key={point.id}>
                        <Table.Td>
                          {formatDate(point.effectiveAt)}
                          {point.supersededAt === null && (
                            <Text size="xs" c="dimmed">
                              what they charge now
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td ta="right" fw={point.supersededAt === null ? 700 : 400}>
                          {formatMoney(point.perUnitMinor, point.currency)}
                        </Table.Td>
                        <Table.Td ta="right">
                          <ChangeBadge point={point} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {point.packQty > 1
                              ? `${formatMoney(point.priceMinor, point.currency)} for ${point.packQty}`
                              : `${formatMoney(point.priceMinor, point.currency)} each`}
                          </Text>
                          {(point.sku || point.description) && (
                            <Text size="xs" c="dimmed">
                              {[point.description, point.sku].filter(Boolean).join(' · ')}
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              {/* Said once, under the table it applies to, rather than as a footnote nobody reads. */}
              {item.entries.some((e) => e.changeUnknownBecause) && (
                <Text size="xs" c="dimmed" mt={4}>
                  {item.entries.find((e) => e.changeUnknownBecause)?.changeUnknownBecause}
                </Text>
              )}
            </div>
          ))}
        </Stack>
      )}
    </Modal>
  );
}
